/**
 * @fileoverview Input/output schemas for the draft-refiner-v2 agent.
 *
 * Style-passive: the agent matches the existing style of the draft and does
 * not consume a style guide or writing sample. Story-aware: receives chapter
 * structural context, plot state, POV, and (selectively) story genre so its
 * edits stay consistent with the chapter's intent and continuity.
 *
 * Primary use: invoked by the `run-refine-v2` composite, which feeds the
 * refiner an editorial brief synthesized from selected findings/weaknesses.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  // The draft + the user's directive (or editorial brief)
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.draftingHistory.schema.shape,
  // Character voice (Voice attribute)
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  // Chapter structural context (key events / pacing intent / POV / boundary)
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  // Continuity context
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  // Genre awareness (only storyContext.genre is referenced in the prompt;
  // the full object is included so the field is in scope for templating).
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
}).partial({
  nextChapterContext: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining the refinement approach, the targeted changes made, and how essential elements (style, voice, structure) were preserved (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The refined chapter draft, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
